import { useLessonReviewMode } from '../hooks/useLessonReviewMode';
// src/components/AssessmentLogin.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAssessmentContext } from '../contexts/AssessmentContext';
import { grades } from '../data/grades';
import { api } from '../api/client';
import {
  AlertTriangle,
  LogIn,
  ArrowRightCircle,
  Lock,
  User,
  BookOpen,
  RefreshCw,
  LogOut,
  UserCog,
  Clock,
  Calendar,
  Ban,
} from 'lucide-react';
import { isEnabledFlag } from '../utils/assessmentIdentity';
import AppFooter from './AppFooter';
import InstructionAccess from './InstructionAccess';
import { deriveLearnerAlias } from '../utils/learnerIdentity';
import {
  balancedRecommendation,
  balancedRecommendationText
} from '../utils/balancedAssessmentRotation';
import { recommendAfterCompletion, sessionBelongsToLearner } from '../utils/nextAssessmentFlow';

/* ──────────────────────────────────────────────────────────────────
   AssessmentLogin Component
─────────────────────────────────────────────────────────────────── */
export default function AssessmentLogin({ onAdminLogin }) {
  /* ---------------- shared context ---------------- */
  const {
    loadAssessmentData,
    setStudentInfo,
    accessCode,
    setSessionContinueCode,
    handleResumeAssessment,
    nextAssessmentIntent,
    clearNextAssessmentIntent,
  } = useAssessmentContext();

  /* ---------------- local state ------------------- */
  const reviewMode=useLessonReviewMode(Boolean(accessCode),accessCode);
  const [visibleAssessments, setVisibleAssessments] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    assessmentType: '',
  });
  const [continueCode, setContinueCode] = useState('');
  const [resumableSessions, setResumableSessions] = useState([]);
  const [selectedResumeCode, setSelectedResumeCode] = useState('');
  const [retainedLearnerIdentity, setRetainedLearnerIdentity] = useState(null);
  const [resumeLookupWarning, setResumeLookupWarning] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRestrictionError, setTimeRestrictionError] = useState('');
  const [isAccessRestricted, setIsAccessRestricted] = useState(false);
  useEffect(()=>{if(reviewMode.enabled){setContinueCode('');setSelectedResumeCode('');}},[reviewMode.enabled,continueCode]);
  useEffect(()=>{if(reviewMode.enabled&&formData.assessmentType&&visibleAssessments.find(row=>String(row.id)===String(formData.assessmentType))?.assessmentKind!=='regular')setFormData(old=>({...old,assessmentType:''}));},[reviewMode.enabled,formData.assessmentType,visibleAssessments]);
  const [isUsingContinueCode, setIsUsingContinueCode] = useState(false);
  const [isUsingStudentInfo, setIsUsingStudentInfo] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recommendedAssessmentId, setRecommendedAssessmentId] = useState('');
  const [assessmentSequenceStatus, setAssessmentSequenceStatus] = useState(null);
  const [repeatAssessmentWarning, setRepeatAssessmentWarning] = useState('');
  const submitInFlight = useRef(false);
  const assessmentNameCounts = useMemo(() => {
    const counts = new Map();
    visibleAssessments.forEach(assessment => {
      const name = String(assessment.name || '').trim().toLowerCase();
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return counts;
  }, [visibleAssessments]);
  const selectedResumeSession = useMemo(() => resumableSessions.find(
    session => String(session.continueCode) === String(selectedResumeCode || continueCode)
  ) || null, [continueCode, resumableSessions, selectedResumeCode]);
  const retainedBlockedAssessmentIds = useMemo(() => {
    if (!retainedLearnerIdentity) return new Set();
    const alias = String(retainedLearnerIdentity.learnerAlias || '').trim();
    const gradeName = String(retainedLearnerIdentity.gradeName || '').trim();
    return new Set(resumableSessions
      .filter(session =>
        sessionBelongsToLearner(session, alias, gradeName, accessCode)
      )
      .map(session => String(session.assessmentId || '').trim())
      .filter(Boolean));
  }, [resumableSessions, retainedLearnerIdentity, accessCode]);
  const recommendedAssessment = useMemo(() => (
    visibleAssessments.find(assessment => String(assessment.id) === String(recommendedAssessmentId))
    || balancedRecommendation(visibleAssessments, retainedBlockedAssessmentIds)
  ), [recommendedAssessmentId, retainedBlockedAssessmentIds, visibleAssessments]);

  const formatResumeTime = value => {
    const date = new Date(value || '');
    if (!Number.isFinite(date.getTime())) return 'Activity time unavailable';
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York', month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short'
    }).format(date);
  };

  const resumeFormData = session => {
    if (!session) return { name: '', grade: '', assessmentType: '' };
    const grade = grades.find(item => item.name === session.actualGradeLevel);
    return {
      name: session.learnerAlias || session.studentName || '',
      grade: grade ? String(grade.id) : '',
      assessmentType: String(session.assessmentId || '')
    };
  };

  const chooseResumeSession = session => {
    if (!session) return;
    setRetainedLearnerIdentity(null);
    setSelectedResumeCode(String(session.continueCode));
    setContinueCode(String(session.continueCode));
    setFormData(resumeFormData(session));
    setError('');
    setRecommendedAssessmentId('');
    setAssessmentSequenceStatus(null);
    setRepeatAssessmentWarning('');
  };

  const useNewAssessmentFlow = (catalog = visibleAssessments, sourceOverride = selectedResumeSession) => {
    const source = sourceOverride;
    const resumed = resumeFormData(source);
    let recommendation = null;
    setSelectedResumeCode('');
    setContinueCode('');
    if (source && resumed.name && resumed.grade) {
      const grade = grades.find(item => String(item.id) === String(resumed.grade));
      const gradeName = grade?.name || String(source.actualGradeLevel || '');
      setRetainedLearnerIdentity({
        learnerAlias: resumed.name,
        gradeId: resumed.grade,
        gradeName,
        continueCode: String(source.continueCode || '')
      });
      const blocked = new Set(resumableSessions
        .filter(session =>
          sessionBelongsToLearner(session, resumed.name, gradeName, accessCode)
        )
        .map(session => String(session.assessmentId || '').trim())
        .filter(Boolean));
      recommendation = balancedRecommendation(catalog, blocked);
      setRecommendedAssessmentId(String(recommendation?.id || ''));
      setFormData({ name: resumed.name, grade: resumed.grade, assessmentType: String(recommendation?.id || '') });
    } else {
      setRetainedLearnerIdentity(null);
      recommendation = balancedRecommendation(catalog);
      setRecommendedAssessmentId(String(recommendation?.id || ''));
      setFormData({ name: '', grade: '', assessmentType: String(recommendation?.id || '') });
    }
    setError(recommendation
      ? ''
      : 'No assigned assessment is currently available. Please check with the teacher.');
    setAssessmentSequenceStatus(null);
    setRepeatAssessmentWarning('');
  };

  const useCompletedAssessmentFlow = async (catalog, sessions) => {
    const learnerAlias = String(nextAssessmentIntent?.learnerAlias || '').trim();
    const gradeName = String(nextAssessmentIntent?.gradeName || '').trim();
    const grade = grades.find(item => item.name === gradeName);
    if (!learnerAlias || !grade) {
      clearNextAssessmentIntent?.();
      useNewAssessmentFlow(catalog, null);
      setError('The previous Learner ID or grade could not be retained. Enter the student information again.');
      return;
    }

    const { assessment: recommendation } = recommendAfterCompletion({
      assessments: catalog,
      sessions,
      learnerAlias,
      gradeName,
      completedAssessmentId: nextAssessmentIntent?.completedAssessmentId,
      accessCode,
    });

    setSelectedResumeCode('');
    setContinueCode('');
    setRetainedLearnerIdentity({
      learnerAlias,
      gradeId: String(grade.id),
      gradeName,
      origin: 'completed',
    });
    setRecommendedAssessmentId(String(recommendation?.id || ''));
    setFormData({
      name: learnerAlias,
      grade: String(grade.id),
      assessmentType: String(recommendation?.id || ''),
    });
    setError(recommendation
      ? ''
      : 'No assigned assessment is currently available. Please check with the teacher.');
    setRepeatAssessmentWarning('');
    try {
      const status = await api.getStudentAssessmentSequenceStatus({
        learnerAlias,
        actualGradeLevel: gradeName,
      });
      setAssessmentSequenceStatus(status);
      const repeated = status.history?.find(
        item => String(item.assessmentId) === String(recommendation?.id || '')
      );
      if (repeated) {
        setRepeatAssessmentWarning(
          `${repeated.assessmentName || recommendation?.name || 'This assessment'} was already completed today. Choose another assessment when available, or confirm this repeat when starting.`
        );
      }
      if (!status.canTakeAnother) {
        setError(status.limitReached
          ? `Today’s limit of ${status.dailyLimit} assessments has been completed. Please check with the teacher.`
          : 'Another assessment is not enabled for today. Please check with the teacher.');
      }
    } catch {
      setAssessmentSequenceStatus(null);
      setResumeLookupWarning('Today’s assessment count could not be refreshed. The server will verify it when Start Assessment is pressed.');
    }
  };

  const returnToResumeFlow = () => {
    const preferred = resumableSessions.find(
      session => String(session.continueCode) === String(retainedLearnerIdentity?.continueCode || '')
    ) || resumableSessions[0];
    if (preferred) chooseResumeSession(preferred);
  };

  /* =====================================================================
      Helper functions for time formatting and EST/EDT detection
  ===================================================================== */
  const getEasternTimeZone = () => {
    const now = new Date();
    const easternTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    
    // Check if we're in Daylight Saving Time
    const january = new Date(easternTime.getFullYear(), 0, 1);
    const july = new Date(easternTime.getFullYear(), 6, 1);
    const janOffset = january.getTimezoneOffset();
    const julOffset = july.getTimezoneOffset();
    const isDST = easternTime.getTimezoneOffset() === Math.min(janOffset, julOffset);
    
    return isDST ? 'EDT' : 'EST';
  };

  const convertTo12Hour = (time24) => {
    if (!time24) return time24;
    
    const [hours, minutes] = time24.split(':');
    const hour24 = parseInt(hours, 10);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  /* ---------------- effect: viewport resize ---------------- */
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  /* ---------------- effect: load assessments ---------------- */
  useEffect(() => {
    loadVisibleAssessments();
  }, [accessCode]);

  /* ---------------- effect: track mode ---------------------- */
  useEffect(() => {
    setIsUsingContinueCode(continueCode.trim() !== '');
    setIsUsingStudentInfo(formData.name.trim() !== '');
  }, [continueCode, formData.name]);

  /* =======================================================================
      Helper functions - FIXED to properly handle "no days allowed"
  ======================================================================= */
  const isWithinAllowedTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return true;
    const estNow = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
    );
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const curMins = estNow.getHours() * 60 + estNow.getMinutes();
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    return startMins > endMins
      ? curMins >= startMins || curMins <= endMins
      : curMins >= startMins && curMins <= endMins;
  };

  // FIXED: Properly handle "no days allowed" scenario
  const isAllowedDay = (daysAllowed) => {
    // If no day restrictions are set, allow access
    if (!daysAllowed && daysAllowed !== '') return true;
    
    // CRITICAL FIX: If daysAllowed is an empty string, it means "no days allowed"
    // This is the "Complete Access Block" scenario from the admin panel
    if (daysAllowed === '') return false;
    
    // Handle array or string format
    let allowedDays = [];
    if (Array.isArray(daysAllowed)) {
      allowedDays = daysAllowed.map(d => parseInt(d, 10));
    } else if (typeof daysAllowed === 'string') {
      // Split by comma and filter out empty strings
      allowedDays = daysAllowed.split(',')
        .map(d => d.trim())
        .filter(d => d !== '')
        .map(d => parseInt(d, 10));
    }
    
    // If after processing we have no allowed days, block access
    if (allowedDays.length === 0) return false;
    
    // Get current day in EST/EDT (1=Monday, 7=Sunday)
    const estDay = new Date(
      new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })
    ).getDay();
    const dayNum = ((estDay + 6) % 7) + 1; // Convert Sunday=0 to Sunday=7 format
    
    return allowedDays.includes(dayNum);
  };

  // Helper function to get day names for error messages
  const getDayNames = (daysAllowed) => {
    const dayMap = {
      1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday',
      5: 'Friday', 6: 'Saturday', 7: 'Sunday'
    };
    
    if (!daysAllowed || daysAllowed === '') return [];
    
    let allowedDays = [];
    if (Array.isArray(daysAllowed)) {
      allowedDays = daysAllowed;
    } else if (typeof daysAllowed === 'string') {
      allowedDays = daysAllowed.split(',').map(d => d.trim()).filter(d => d !== '');
    }
    
    return allowedDays.map(d => dayMap[d] || d).filter(Boolean);
  };

  /* =======================================================================
      Load assessments visible to this user
  ======================================================================= */
  const loadVisibleAssessments = async () => {
    if (!accessCode) {
      setVisibleAssessments([]);
      setResumableSessions([]);
      setSelectedResumeCode('');
      setContinueCode('');
      setRetainedLearnerIdentity(null);
      setIsAccessRestricted(false);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setTimeRestrictionError('');
      setResumeLookupWarning('');
      setIsAccessRestricted(false);
      let accessRestricted = false;

      /* ----- verify user & check restrictions ----- */
      const user = await api.verifyAccessCode(accessCode);

      /* ENHANCED: Day restriction check with proper "no days allowed" handling */
      if (user && isEnabledFlag(user.restrictDays)) {
        const isAllowed = isAllowedDay(user.daysAllowed);
        
        if (!isAllowed) {
          const dayNames = getDayNames(user.daysAllowed);
          
          let restrictionMessage;
          if (dayNames.length === 0) {
            // This is the "Complete Access Block" scenario
            restrictionMessage = `🚫 Assessment access is completely blocked. No days are allowed for assessment access. Please contact your administrator.`;
          } else {
            // Regular day restriction
            restrictionMessage = `Assessment access is not available today. Access is only allowed on: ${dayNames.join(', ')}`;
          }
          
          setTimeRestrictionError(restrictionMessage);
          setIsAccessRestricted(true);
          accessRestricted = true;
        }
      }

      /* Time restriction - FIXED with proper EST/EDT detection and 12-hour format */
      if (
        user &&
        isEnabledFlag(user.restrictTime) &&
        !isWithinAllowedTimeRange(user.accessStartTime, user.accessEndTime)
      ) {
        const timeZone = getEasternTimeZone();
        const startTime12 = convertTo12Hour(user.accessStartTime);
        const endTime12 = convertTo12Hour(user.accessEndTime);
        setTimeRestrictionError(
          `Assessment access is restricted. Your access hours are: ${startTime12} to ${endTime12} ${timeZone}`
        );
        setIsAccessRestricted(true);
        accessRestricted = true;
      }

      if (accessRestricted) {
        setVisibleAssessments([]);
        return;
      }

      // One server-side ID join returns only assessments assigned and visible
      // to this exact main/sub-user, including its TTS and timer configuration.
      const [catalogResult, resumeResult] = await Promise.allSettled([
        api.getAvailableAssessments(accessCode),
        ['main_user', 'subuser'].includes(user?.role)
          ? api.getResumableSessions()
          : Promise.resolve([])
      ]);
      if (catalogResult.status !== 'fulfilled') throw catalogResult.reason;
      const catalog = catalogResult.value;
      const eligibleCatalog = Array.isArray(catalog)
        ? catalog.filter(item => isEnabledFlag(item.is_assigned) && isEnabledFlag(item.is_visible))
        : [];
      setVisibleAssessments(eligibleCatalog);
      if (resumeResult.status === 'fulfilled') {
        const sessions = Array.isArray(resumeResult.value) ? resumeResult.value : [];
        setResumableSessions(sessions);
        if (nextAssessmentIntent) await useCompletedAssessmentFlow(eligibleCatalog, sessions);
        else if (sessions[0]) chooseResumeSession(sessions[0]);
        else useNewAssessmentFlow(eligibleCatalog, null);
      } else {
        setResumableSessions([]);
        setSelectedResumeCode('');
        setContinueCode('');
        if (nextAssessmentIntent) await useCompletedAssessmentFlow(eligibleCatalog, []);
        else useNewAssessmentFlow(eligibleCatalog, null);
        setResumeLookupWarning('Saved assessments could not be checked automatically. You may enter a Continue Code or start a new assessment.');
      }
    } catch (e) {
      console.error('Error loading assessments:', e);
      // Visibility is security-sensitive: a failed settings request must never
      // expose the full assessment catalog.
      setVisibleAssessments([]);
      setError('Assigned assessments could not be loaded. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================================
      Form handlers
  ======================================================================= */
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'continueCode') {
      setRetainedLearnerIdentity(null);
      if (value.trim() !== '' && !isUsingStudentInfo) {
        setFormData({ name: '', grade: '', assessmentType: '' });
      }
      setContinueCode(value);
      const matchingSession = resumableSessions.find(session => String(session.continueCode) === value.trim());
      if (matchingSession) {
        setSelectedResumeCode(String(matchingSession.continueCode));
        setFormData(resumeFormData(matchingSession));
      } else {
        setSelectedResumeCode('');
        setFormData({ name: '', grade: '', assessmentType: '' });
      }
    } else {
      if (retainedLearnerIdentity && (name === 'name' || name === 'grade')) return;
      if (name === 'name' && value.trim() !== '' && !isUsingContinueCode) {
        setContinueCode('');
      }
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (name === 'assessmentType' && assessmentSequenceStatus) {
        const repeated = assessmentSequenceStatus.history?.find(
          item => String(item.assessmentId) === String(value)
        );
        setRepeatAssessmentWarning(repeated
          ? `${repeated.assessmentName} was already completed today. Choose another assessment unless the teacher requested a retest.`
          : '');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if(reviewMode.enabled||!reviewMode.known||Boolean(reviewMode.error)){setError(reviewMode.enabled?'Lesson review time. Choose Study my Lessons.':'Checking assessment access. Please retry shortly.');return;}
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setIsSubmitting(true);

    const releaseSubmitLock = () => {
      submitInFlight.current = false;
      setIsSubmitting(false);
    };

    /* ---------- resume flow ---------- */
    if (continueCode.trim() !== '') {
      try {
        // All resume entry points use the same deterministic restoration path.
        await handleResumeAssessment(continueCode.trim());
      } catch (err) {
        setError(err?.message || 'Failed to resume assessment. Please check your Continue Code.');
        console.error('Resume error:', err);
        releaseSubmitLock();
      }
      return;
    }

        /* ---------- new session flow ---------- */
    if (isAccessRestricted) {
      setError('Assessment access is currently restricted based on your schedule settings.');
      releaseSubmitLock();
      return;
    }
    if (!formData.name.trim() || !formData.grade || !formData.assessmentType) {
      setError('Please fill in all fields');
      releaseSubmitLock();
      return;
    }

    const gradeObj = grades.find((g) => g.id.toString() === formData.grade.toString());
    const gradeName = retainedLearnerIdentity?.gradeName || (gradeObj ? gradeObj.name : formData.grade);
    
    // The entered name is transformed in-browser and is never transmitted or
    // retained by AssessNow. Only the coded Learner ID enters application state.
    const learnerAlias = retainedLearnerIdentity?.learnerAlias || deriveLearnerAlias(formData.name, gradeName, accessCode);
    if (!learnerAlias) {
      setError('A Learner ID could not be created. Check the name, grade, and access code.');
      releaseSubmitLock();
      return;
    }
    setStudentInfo(learnerAlias, gradeName, formData.assessmentType);
    setFormData(current => ({ ...current, name: learnerAlias }));

    try {
      const selected = visibleAssessments.find(
        (assessment) => String(assessment.id) === String(formData.assessmentType)
      );
      if (!selected) {
        setError('Please select a valid assessment type.');
        releaseSubmitLock();
        return;
      }
      if (retainedLearnerIdentity && retainedBlockedAssessmentIds.has(String(selected.id))) {
        setError('This assessment already has an unfinished attempt for this Learner ID. Resume that attempt or select a different assessment.');
        releaseSubmitLock();
        return;
      }
      const repeatedToday = assessmentSequenceStatus?.history?.some(
        item => String(item.assessmentId) === String(selected.id)
      );
      if (repeatedToday && !window.confirm(
        `${selected.name} was already completed today. Start it again only if the teacher requested a retest. Continue?`
      )) {
        releaseSubmitLock();
        return;
      }

      /* Create session */
      const sessionResponse = await api.post('/sessions', {
        accessCode,
        learnerAlias,
        actualGradeLevel: gradeName,
        assessmentId: selected.id,
        lastQuestionIndex: 0,
      });
      const newCode = sessionResponse.continueCode;
      if (newCode) {
        setSessionContinueCode?.(newCode);
      }
      if (sessionResponse.reused) {
        // Starting the same unfinished assessment is a resume, even when the
        // learner used the name/grade form instead of typing the Continue Code.
        await handleResumeAssessment(newCode);
        clearNextAssessmentIntent?.();
        return;
      }

      await loadAssessmentData(selected.fileName, selected, {
        studentName: learnerAlias,
        studentGrade: gradeName,
        continueCode: newCode,
        viewEpoch: sessionResponse.viewEpoch || '',
      });
      clearNextAssessmentIntent?.();
    } catch (err) {
      setError(
        ['ASSESSMENT_DAILY_LIMIT_REACHED', 'ASSESSMENT_SEQUENCE_DISABLED'].includes(err?.code)
          ? err.message
          : err?.code === 'PERFORMANCE_IDENTITY_CONFLICT'
            ? 'This start link is already connected to a different attempt. Return here and start the assessment again to create a new secure session.'
            : 'Failed to load assessment. No answers were accepted. Please try again.'
      );
      console.error('Assessment loading error:', err);
      releaseSubmitLock();
    }
  };

  const handleExit = () => {
    if (isSubmitting) return;
    const hasSelections = Boolean(
      formData.name.trim() || formData.grade || formData.assessmentType || continueCode.trim()
    );
    if (hasSelections && !window.confirm('Exit without starting? Your current selections will be cleared.')) {
      return;
    }
    window.location.assign('/');
  };

  const handleAdminLogin = () => onAdminLogin?.();

  /* =======================================================================
      Styles (unchanged except wider maxWidth on boxStyle)
  ======================================================================= */
  const containerStyle = {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(to bottom, #0f172a, #1e293b, #334155)',
    padding: isMobileView ? '1rem' : '2rem',
    boxSizing: 'border-box',
    gap: '1.5rem',
    position: 'relative',
    overflow: 'hidden',
  };

  const blobContainerStyle = {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: 0,
  };

  const boxStyle = {
    width: '100%',
    maxWidth: '700px',
    backgroundColor: 'rgba(15, 23, 42, 0.8)',
    borderRadius: '1rem',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2), 0 5px 10px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    backdropFilter: 'blur(12px)',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    color: 'white',
    animation: 'fadeIn 0.4s ease',
    zIndex: 1,
  };

  const assessmentBoxStyle = {
    ...boxStyle,
    ...(isAccessRestricted && {
      opacity: 0.9,
      backgroundColor: 'rgba(30, 41, 59, 0.8)',
      boxShadow: '0 10px 25px rgba(220, 38, 38, 0.1), 0 5px 10px rgba(220, 38, 38, 0.05)',
      pointerEvents: 'none',
      position: 'relative',
      borderColor: 'rgba(239, 68, 68, 0.3)',
    }),
  };

  const formContainerStyle = {
    padding: isMobileView ? '1.5rem' : '2.5rem',
  };

  const titleStyle = {
    fontSize: isMobileView ? '1.75rem' : '2rem',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '1.5rem',
    color: '#e2e8f0',
    borderBottom: '2px solid rgba(148, 163, 184, 0.2)',
    paddingBottom: '1rem',
  };

  const formGroupStyle = {
    marginBottom: '1.25rem',
  };

  const labelStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.95rem',
    fontWeight: '500',
    marginBottom: '0.5rem',
    color: '#94a3b8',
  };

  const inputBaseStyle = {
    width: '100%',
    padding: '0.875rem',
    fontSize: '1rem',
    border: '1px solid #cbd5e1',
    borderRadius: '0.5rem',
    boxSizing: 'border-box',
    backgroundColor: '#ffffff',
    transition: 'all 0.2s ease',
    color: '#111827',
    WebkitTextFillColor: '#111827',
  };

  const inputStyle = {
    ...inputBaseStyle,
  };

  const disabledInputStyle = {
    ...inputBaseStyle,
    backgroundColor: '#f1f5f9',
    cursor: 'not-allowed',
    opacity: 1,
    color: '#475569',
    WebkitTextFillColor: '#475569',
    border: '1px solid #cbd5e1',
  };

  const buttonBaseStyle = {
    width: '100%',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: '600',
    border: 'none',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  };

  const primaryButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#10b981',
    backgroundImage: 'linear-gradient(to right, #10b981, #0d9488)',
    color: 'white',
    minHeight: isMobileView ? '72px' : '68px',
    padding: isMobileView ? '1.15rem 1.25rem' : '1rem 1.25rem',
    fontSize: isMobileView ? '1.15rem' : '1.1rem',
    fontWeight: '800',
    border: '2px solid rgba(167, 243, 208, 0.5)',
    boxShadow: '0 8px 20px rgba(16, 185, 129, 0.30)',
    marginTop: '1.5rem',
  };

  const exitButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#475569',
    color: 'white',
    minHeight: '48px',
    marginTop: '1.15rem',
  };

  const adminButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: '#f97316',
    backgroundImage: 'linear-gradient(to right, #f97316, #ea580c)',
    color: 'white',
    boxShadow: '0 4px 10px rgba(234, 88, 12, 0.28)',
  };

  const errorBaseStyle = {
    padding: '0.75rem',
    borderRadius: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    fontSize: '0.9rem',
    fontWeight: '500',
  };

  const errorStyle = {
    ...errorBaseStyle,
    backgroundColor: 'rgba(153, 27, 27, 0.3)',
    color: '#fecaca',
    border: '1px solid rgba(220, 38, 38, 0.3)',
  };

  // ENHANCED: Different styling for "complete block" vs regular time restrictions
  const getTimeRestrictionErrorStyle = () => {
    const isCompleteBlock = timeRestrictionError.includes('🚫') && timeRestrictionError.includes('completely blocked');
    
    return {
      width: '100%',
      maxWidth: '700px',
      backgroundColor: isCompleteBlock ? 'rgba(127, 29, 29, 0.4)' : 'rgba(153, 27, 27, 0.3)',
      borderRadius: '0.75rem',
      color: '#fecaca',
      padding: '1rem',
      boxShadow: isCompleteBlock 
        ? '0 4px 6px rgba(220, 38, 38, 0.2), 0 0 15px rgba(220, 38, 38, 0.1)' 
        : '0 4px 6px rgba(220, 38, 38, 0.1)',
      border: isCompleteBlock 
        ? '2px solid rgba(220, 38, 38, 0.4)' 
        : '1px solid rgba(220, 38, 38, 0.3)',
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.75rem',
      backdropFilter: 'blur(8px)',
      zIndex: 1,
    };
  };

  const timeIconContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.9rem',
    color: '#94a3b8',
    marginTop: '0.5rem',
  };

  /* =======================================================================
      Add animations stylesheet once
  ======================================================================= */
  useEffect(() => {
    if (!document.querySelector('#assessment-login-styles')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'assessment-login-styles';
      styleSheet.textContent = `
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(16, 185, 129, 0); }
          100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0); }
        }
        @keyframes blockPulse {
          0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
          100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }
        .input-focused:focus {
          border-color: #10b981 !important;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25) !important;
          outline: none !important;
        }
        .input-focused::placeholder {
          color: #64748b !important;
          opacity: 1;
        }
        select.input-focused option {
          background: #ffffff;
          color: #111827;
        }
        .hover-effect:hover {
          background-color: #0d9488 !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 10px rgba(13, 148, 136, 0.3) !important;
        }
        .hover-effect-secondary:hover {
          background-color: #334155 !important;
          transform: translateY(-1px) !important;
        }
        .teacher-login-button:hover {
          background-color: #ea580c !important;
          background-image: linear-gradient(to right, #ea580c, #c2410c) !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 14px rgba(234, 88, 12, 0.38) !important;
        }
        .teacher-login-button:focus-visible {
          outline: 3px solid #fdba74 !important;
          outline-offset: 3px !important;
        }
        .hover-effect-danger:hover {
          background-color: #b91c1c !important;
        }
        .restricted-pulse {
          animation: pulse 2s infinite;
        }
        .blocked-pulse {
          animation: blockPulse 2s infinite;
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  /* =======================================================================
      JSX
  ======================================================================= */
  return (
    <div style={containerStyle}>
      {/* Decorative blobs */}
      <div style={blobContainerStyle}>
        <div
          style={{
            position: 'absolute',
            top: '10%',
            left: '25%',
            width: '25rem',
            height: '25rem',
            backgroundColor: 'rgba(16, 185, 129, 0.07)',
            borderRadius: '50%',
            filter: 'blur(70px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            bottom: '20%',
            right: '25%',
            width: '20rem',
            height: '20rem',
            backgroundColor: 'rgba(13, 148, 136, 0.07)',
            borderRadius: '50%',
            filter: 'blur(70px)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '40%',
            right: '30%',
            width: '15rem',
            height: '15rem',
            backgroundColor: 'rgba(139, 92, 246, 0.05)',
            borderRadius: '50%',
            filter: 'blur(70px)',
          }}
        />
      </div>

      {/* ENHANCED: Time / Day restriction banner with different styling for complete blocks */}
      {timeRestrictionError && (
        <div style={getTimeRestrictionErrorStyle()}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {timeRestrictionError.includes('🚫') ? (
              <Ban size={24} color="#fecaca" />
            ) : (
              <AlertTriangle size={24} color="#fecaca" />
            )}
            <span style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
              {timeRestrictionError}
            </span>
          </div>
          <div style={{ fontSize: '0.95rem', lineHeight: '1.4' }}>
            {timeRestrictionError.includes('completely blocked') 
              ? 'This account has been set to block all assessment access.'
              : 'Please use Teacher Login below to continue.'
            }
          </div>
          <div style={timeIconContainerStyle}>
            <Clock size={14} />
            <span>Current time is set to Eastern Time ({getEasternTimeZone()}).</span>
          </div>
        </div>
      )}

      {/* Main form box */}
      <div style={assessmentBoxStyle}>
        {isAccessRestricted && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 10,
              cursor: 'not-allowed',
            }}
          />
        )}

        <div style={formContainerStyle}>
          <h1 style={titleStyle}>Student Assessment</h1>

          <form onSubmit={handleSubmit}>
            {/* Student Name */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                <User size={16} />
                {isUsingContinueCode || retainedLearnerIdentity ? 'Learner ID' : 'Student Name'}
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                style={isUsingContinueCode || retainedLearnerIdentity ? disabledInputStyle : inputStyle}
                className="input-focused"
                placeholder="Enter your full name"
                disabled={isAccessRestricted || isUsingContinueCode || Boolean(retainedLearnerIdentity)}
              />
            </div>

            {/* Grade */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                <BookOpen size={16} />
                Grade Level
              </label>
              <select
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                style={
                  loading ||
                  isAccessRestricted ||
                  isUsingContinueCode ||
                  retainedLearnerIdentity ||
                  formData.name.trim() === ''
                    ? disabledInputStyle
                    : inputStyle
                }
                className="input-focused"
                disabled={
                  loading ||
                  isAccessRestricted ||
                  isUsingContinueCode ||
                  Boolean(retainedLearnerIdentity) ||
                  formData.name.trim() === ''
                }
              >
                <option value="">Select grade</option>
                {grades.map((grade) => (
                  <option key={grade.id} value={grade.id}>
                    {grade.name}
                  </option>
                ))}
              </select>
              {!isUsingContinueCode && recommendedAssessment && (
                <div role="status" style={{ marginTop: '.45rem', color: '#fde68a', fontSize: '.82rem', lineHeight: 1.35 }}>
                  <strong>{balancedRecommendationText(recommendedAssessment)}</strong>
                  <span style={{ display: 'block', color: '#cbd5e1' }}>You may choose another assigned assessment.</span>
                </div>
              )}
              {!loading && !isUsingContinueCode && visibleAssessments.length === 0 && (
                <div role="status" style={{ marginTop: '.45rem', color: '#fecaca', fontSize: '.82rem' }}>
                  No assigned assessments are currently available.
                </div>
              )}
            </div>

            {/* Assessment Name */}
            <div style={formGroupStyle}>
              <label style={labelStyle}>
                <BookOpen size={16} />
                Assessment Name
              </label>
              <select
                name="assessmentType"
                value={formData.assessmentType}
                onChange={handleChange}
                style={
                  loading ||
                  isAccessRestricted ||
                  isUsingContinueCode ||
                  formData.grade === ''
                    ? disabledInputStyle
                    : inputStyle
                }
                className="input-focused"
                disabled={
                  loading ||
                  isAccessRestricted ||
                  isUsingContinueCode ||
                  (!reviewMode.enabled && formData.grade === '')
                }
              >
                <option value="">Select assessment name</option>
                {visibleAssessments.map((type) => {
                  const hasUnfinishedAttempt = Boolean(retainedLearnerIdentity) &&
                    retainedBlockedAssessmentIds.has(String(type.id));
                  return (
                  <option key={type.id} value={type.id} disabled={(reviewMode.enabled&&type.assessmentKind!=='regular')||(!reviewMode.enabled&&hasUnfinishedAttempt)}>
                    {type.name}
                    {assessmentNameCounts.get(String(type.name || '').trim().toLowerCase()) > 1
                      ? ` — ${type.fileName || type.id}`
                      : ''}
                    {reviewMode.enabled&&type.assessmentKind!=='regular'?' — available when assessment mode resumes':hasUnfinishedAttempt&&!reviewMode.enabled?' — unfinished; resume instead':''}
                  </option>
                  );
                })}
              </select>
              {assessmentSequenceStatus && !isUsingContinueCode && !reviewMode.enabled && (
                <div role="status" style={{ marginTop: '.55rem', padding: '.65rem .75rem', borderRadius: '.5rem', background: 'rgba(30,64,175,.18)', border: '1px solid rgba(96,165,250,.35)', color: '#dbeafe', fontSize: '.82rem', lineHeight: 1.4 }}>
                  <strong>{assessmentSequenceStatus.completedToday} of {assessmentSequenceStatus.dailyLimit} completed today</strong>
                  <span style={{ display: 'block', color: '#bfdbfe' }}>
                    {assessmentSequenceStatus.canTakeAnother
                      ? `${assessmentSequenceStatus.remainingToday} assessment${assessmentSequenceStatus.remainingToday === 1 ? '' : 's'} remaining today.`
                      : assessmentSequenceStatus.limitReached
                        ? 'Today’s assessment limit has been reached.'
                        : 'Another assessment is not enabled for today.'}
                  </span>
                </div>
              )}
              {repeatAssessmentWarning && (
                <div role="alert" style={{ marginTop: '.55rem', padding: '.65rem .75rem', borderRadius: '.5rem', background: 'rgba(146,64,14,.24)', border: '1px solid rgba(251,191,36,.45)', color: '#fde68a', fontSize: '.82rem', lineHeight: 1.4 }}>
                  {repeatAssessmentWarning}
                </div>
              )}
            </div>

            {retainedLearnerIdentity && !isUsingContinueCode && (
              <div role="status" style={{ marginBottom: '1rem', padding: '.8rem', borderRadius: '.5rem', background: 'rgba(14,116,144,.16)', border: '1px solid rgba(34,211,238,.35)', color: '#cffafe', lineHeight: 1.45 }}>
                <strong style={{ display: 'block', color: '#67e8f9' }}>Starting a new assessment</strong>
                <span style={{ display: 'block', fontSize: '.84rem' }}>
                  {retainedLearnerIdentity.origin === 'completed'
                    ? 'Your private Learner ID and verified grade level were retained. AssessNow selected the next recommended assigned assessment; you may choose another.'
                    : 'Your private Learner ID and verified grade level were retained. Select another assigned assessment below. The unfinished assessment remains available to resume later.'}
                </span>
                {resumableSessions.some(session =>
                  sessionBelongsToLearner(session, retainedLearnerIdentity.learnerAlias, retainedLearnerIdentity.gradeName, accessCode)
                ) && (
                  <button type="button" onClick={returnToResumeFlow} style={{ marginTop: '.45rem', border: 0, padding: 0, background: 'transparent', color: '#fde68a', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                    Return to Resume Assessment
                  </button>
                )}
              </div>
            )}

            {/* Continue Code */}
            {!isSubmitting && !reviewMode.enabled && (
              <div
                style={{
                  ...formGroupStyle,
                  padding: '0.75rem',
                  backgroundColor: isUsingContinueCode
                    ? 'rgba(6, 78, 59, 0.3)'
                    : 'rgba(51, 65, 85, 0.3)',
                  borderRadius: '0.5rem',
                  marginTop: '1.5rem',
                  border: isUsingContinueCode
                    ? '1px solid rgba(16, 185, 129, 0.3)'
                    : '1px solid rgba(148, 163, 184, 0.2)',
                }}
              >
                <label
                  style={{
                    ...labelStyle,
                    color: isUsingContinueCode ? '#34d399' : '#94a3b8',
                    fontWeight: isUsingContinueCode ? '600' : '500',
                  }}
                >
                  <RefreshCw size={16} />
                  Continue Code (Optional)
                </label>
                {resumableSessions.length > 1 && (
                  <select
                    value={selectedResumeCode}
                    onChange={event => chooseResumeSession(resumableSessions.find(session => String(session.continueCode) === event.target.value))}
                    aria-label="Choose an assessment to resume"
                    style={{ ...inputStyle, marginBottom: '0.65rem', backgroundColor: '#ecfdf5', color: '#064e3b', WebkitTextFillColor: '#064e3b' }}
                  >
                    {resumableSessions.map(session => (
                      <option key={session.id} value={session.continueCode}>
                        {session.assessmentName} — {session.continueCode} — {formatResumeTime(session.lastActivityAt)}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="text"
                  name="continueCode"
                  value={continueCode}
                  onChange={handleChange}
                  style={{
                    ...inputStyle,
                    borderColor: isUsingContinueCode
                      ? 'rgba(16, 185, 129, 0.5)'
                      : 'rgba(148, 163, 184, 0.3)',
                    backgroundColor: isUsingStudentInfo && !selectedResumeSession ? '#f1f5f9' : '#ffffff',
                    color: isUsingStudentInfo && !selectedResumeSession ? '#475569' : '#111827',
                    WebkitTextFillColor: isUsingStudentInfo && !selectedResumeSession ? '#475569' : '#111827',
                    opacity: 1,
                  }}
                  className="input-focused"
                  placeholder="Enter your continue code to resume"
                  disabled={isAccessRestricted || (isUsingStudentInfo && !selectedResumeSession)}
                />
                {selectedResumeSession && (
                  <div style={{ marginTop: '0.7rem', padding: '0.75rem', borderRadius: '0.5rem', background: 'rgba(16,185,129,.12)', border: '1px solid rgba(52,211,153,.35)', color: '#d1fae5', lineHeight: 1.45 }}>
                    <strong style={{ display: 'block', color: '#6ee7b7', fontSize: '1rem' }}>{selectedResumeSession.assessmentName}</strong>
                    <span style={{ display: 'block', fontSize: '.84rem' }}>Learner ID: {selectedResumeSession.learnerAlias || selectedResumeSession.studentName} • {selectedResumeSession.actualGradeLevel}</span>
                    <span style={{ display: 'block', fontSize: '.78rem', color: '#a7f3d0' }}>Your private Learner ID was restored. AssessNow does not retain your entered name.</span>
                    <span style={{ display: 'block', fontSize: '.84rem' }}>Continue Code: {selectedResumeSession.continueCode} • Resume at Question {selectedResumeSession.lastQuestionIndex}</span>
                    <span style={{ display: 'block', fontSize: '.78rem', color: '#a7f3d0' }}>Last active: {formatResumeTime(selectedResumeSession.lastActivityAt)}</span>
                    {selectedResumeSession.assignmentAvailable === false && (
                      <span style={{ display: 'block', marginTop: '.45rem', fontSize: '.8rem', color: '#fde68a', fontWeight: 700 }}>
                        This assessment is closed to new attempts, but this saved attempt remains available for completion.
                      </span>
                    )}
                  </div>
                )}
                {continueCode.trim() !== '' && (
                  <button type="button" onClick={() => useNewAssessmentFlow()} style={{ marginTop: '.65rem', border: 0, padding: 0, background: 'transparent', color: '#fbbf24', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                    Start a New Assessment Instead
                  </button>
                )}
              </div>
            )}

            {resumeLookupWarning && <div role="status" style={{ ...errorStyle, backgroundColor: 'rgba(120,53,15,.35)', borderColor: 'rgba(251,191,36,.45)' }}><AlertTriangle size={16} color="#fde68a" /><span>{resumeLookupWarning}</span></div>}

            {/* Error message */}
            {error && (
              <div style={errorStyle}>
                <AlertTriangle size={16} color="#fecaca" />
                <span>{error}</span>
              </div>
            )}

            {reviewMode.enabled&&<div className="review-student-notice" role="status"><strong>Lesson review time</strong><p>Choose a regular assessment and Study my Lessons. Assessment starts and resumes are paused.</p></div>}
            {reviewMode.error&&<p role="alert">{reviewMode.error}</p>}
            {/* Submit & Exit buttons */}
            {!continueCode.trim() && formData.assessmentType && (!isAccessRestricted||reviewMode.enabled) && <InstructionAccess
              phase="before" assessmentId={formData.assessmentType} reviewOnly={reviewMode.enabled} />}
            <button
              type="submit"
              style={{
                ...primaryButtonStyle,
                opacity: reviewMode.enabled || !reviewMode.known || Boolean(reviewMode.error) || isAccessRestricted || isSubmitting || (!continueCode.trim() && assessmentSequenceStatus?.canTakeAnother === false) ? 0.5 : 1,
                cursor: reviewMode.enabled || !reviewMode.known || Boolean(reviewMode.error) || isAccessRestricted || isSubmitting || (!continueCode.trim() && assessmentSequenceStatus?.canTakeAnother === false) ? 'not-allowed' : 'pointer',
                pointerEvents: reviewMode.enabled || !reviewMode.known || Boolean(reviewMode.error) || isAccessRestricted || isSubmitting || (!continueCode.trim() && assessmentSequenceStatus?.canTakeAnother === false) ? 'none' : 'auto',
              }}
              className={!reviewMode.enabled && reviewMode.known && !reviewMode.error && !isAccessRestricted && !isSubmitting && (continueCode.trim() || assessmentSequenceStatus?.canTakeAnother !== false) ? 'hover-effect student-primary-action' : 'student-primary-action'}
              disabled={reviewMode.enabled || !reviewMode.known || Boolean(reviewMode.error) || isAccessRestricted || isSubmitting || (!continueCode.trim() && assessmentSequenceStatus?.canTakeAnother === false)}
              aria-busy={isSubmitting}
            >
              {reviewMode.enabled ? 'Assessment paused · study lessons' : isSubmitting ? (
                <>
                  <RefreshCw size={24} aria-hidden="true" />
                  {continueCode.trim() !== '' ? 'Resuming…' : 'Starting…'}
                </>
              ) : continueCode.trim() !== '' ? (
                <>
                  <RefreshCw size={24} aria-hidden="true" />
                  Resume Assessment
                </>
              ) : (
                <>
                  <ArrowRightCircle size={24} aria-hidden="true" />
                  Start Assessment
                </>
              )}
            </button>
            <button
              type="button"
              style={exitButtonStyle}
              className="hover-effect-secondary"
              onClick={handleExit}
              disabled={isSubmitting}
            >
              <LogOut size={18} />
              Exit
            </button>
          </form>
        </div>
      </div>

      {/* ENHANCED: Admin login & exit with different styling for complete blocks */}
      {onAdminLogin && (
        <div
          style={{
            ...boxStyle,
            padding: '1.25rem',
            backgroundColor: isAccessRestricted
              ? (timeRestrictionError.includes('completely blocked') 
                  ? 'rgba(127, 29, 29, 0.3)' 
                  : 'rgba(6, 78, 59, 0.5)')
              : 'rgba(15, 23, 42, 0.8)',
            borderColor: isAccessRestricted
              ? (timeRestrictionError.includes('completely blocked')
                  ? 'rgba(220, 38, 38, 0.4)'
                  : 'rgba(16, 185, 129, 0.4)')
              : 'rgba(148, 163, 184, 0.2)',
            borderWidth: isAccessRestricted ? '2px' : '1px',
            borderStyle: 'solid',
          }}
        >
          <button
            onClick={handleAdminLogin}
            aria-label="Open Teacher Login"
            style={{
              ...adminButtonStyle,
              backgroundColor: isAccessRestricted 
                ? (timeRestrictionError.includes('completely blocked')
                    ? '#dc2626'
                    : '#f97316')
                : '#f97316',
              backgroundImage: isAccessRestricted
                ? (timeRestrictionError.includes('completely blocked')
                    ? 'linear-gradient(to right, #dc2626, #b91c1c)'
                    : 'linear-gradient(to right, #f97316, #ea580c)')
                : 'linear-gradient(to right, #f97316, #ea580c)',
              boxShadow: isAccessRestricted
                ? (timeRestrictionError.includes('completely blocked')
                    ? '0 0 15px rgba(220, 38, 38, 0.4)'
                    : '0 4px 12px rgba(234, 88, 12, 0.35)')
                : '0 4px 10px rgba(234, 88, 12, 0.28)',
            }}
            className={
              isAccessRestricted 
                ? (timeRestrictionError.includes('completely blocked') 
                    ? 'blocked-pulse' 
                    : 'teacher-login-button')
                : 'teacher-login-button'
            }
          >
            <UserCog size={18} />
            {timeRestrictionError.includes('completely blocked') 
              ? 'Admin Override Required'
              : 'Teacher Login'
            }
          </button>

          {isAccessRestricted && (
            <button
              type="button"
              style={{
                ...exitButtonStyle,
                marginTop: '1rem',
                backgroundColor: '#b91c1c',
              }}
              className="hover-effect-danger"
              onClick={() => (window.location.href = '/')}
            >
              <LogOut size={18} />
              Exit Application
            </button>
          )}
        </div>
      )}
      <AppFooter />
    </div>
  );
}
